import { useState } from 'react';
import { IconButton, Menu, TextField, Button, Typography, Box, Fade, Divider, Zoom } from '@mui/material';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { toast } from 'sonner';
import { ChevronDown } from 'lucide-react';

export default function CounterNo() {
  const [count, setCount] = useState(() => {
    const saved = localStorage.getItem('counterno');
    return saved !== null ? Number(saved) : 0;
  });

  const [temp, setTemp] = useState(count);
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  const handleOpen = (e) => {
    setTemp(count);
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSave = () => {
    let value = Number(temp) || 0;

    if (value > 999) {
      toast.error('Counter number cannot exceed 999');
      return;
    }

    setCount(value);
    localStorage.setItem('counterno', value);
    toast.success(`Counter #${value} set successfully`);
    handleClose();
  };

  const increment = () => {
    setTemp((prev) => Math.min(prev + 1, 999));
  };

  const decrement = () => {
    setTemp((prev) => Math.max(prev - 1, 0));
  };

  return (
    <>
      <Zoom in={true} style={{ transitionDelay: '100ms' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 0.8,
            mx: { xs: 0, lg: 1 },
            borderRadius: 2,
            cursor: 'pointer',
            transition: 'all 0.2s',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': {
              bgcolor: 'action.hover',
              borderColor: 'primary.main',
              transform: 'translateY(-1px)',
              boxShadow: 1
            }
          }}
          onClick={handleOpen}
        >
          <ConfirmationNumberIcon size={18} />
          <Typography
            variant="body2"
            sx={{
              opacity: 0.8,
              fontSize: 10,
              display: 'block',
              lineHeight: 1.2
            }}
          >
            Counter
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              fontSize: 16,
              lineHeight: 1.2,
              letterSpacing: '0.5px'
            }}
          >
            #{count}
          </Typography>
          <ChevronDown
            size={16}
            style={{
              transition: 'transform 0.2s',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
            }}
          />
        </Box>
      </Zoom>

      {/* Modern Menu Dialog */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        TransitionComponent={Fade}
        transitionDuration={300}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            width: 320,
            p: 0,
            borderRadius: 3,
            mt: 1.5,
            overflow: 'visible',
            backgroundColor: 'var(--appMainMain)',
            boxShadow: '0 20px 40px var(--appShadowDark), 0 0 0 1px var(--appBorderMedium)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -10,
              right: 20,
              width: 16,
              height: 16,
              backgroundColor: 'var(--appMainMain)',
              transform: 'rotate(45deg)',
              borderTop: '1px solid var(--appBorderMedium)',
              borderLeft: '1px solid var(--appBorderMedium)',
              zIndex: 0
            }
          }
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            backgroundColor: 'var(--appMainMain)',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            color: 'var(--appMainWhite)'
          }}
        >
          <Typography variant="h5" sx={{ opacity: 0.9, mb: 0.5 }}>
            Current Counter
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 'bold', letterSpacing: 1 }}>
            #{count}
          </Typography>
        </Box>

        <Box sx={{ p: 2.5, backgroundColor: 'var(--appSurface)' }}>
          {/* Modern Counter Input */}
          <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500, color: 'var(--appTextSecondary)' }}>
            Set Counter Number
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              justifyContent: 'center',
              mb: 3
            }}
          >
            <IconButton
              onClick={decrement}
              sx={{
                bgcolor: 'var(--appGrey100)',
                '&:hover': { bgcolor: 'var(--appGrey200)' },
                width: 48,
                height: 48
              }}
            >
              <KeyboardArrowDownIcon />
            </IconButton>

            <TextField
              size="medium"
              type="number"
              value={temp}
              onChange={(e) => {
                let val = parseInt(e.target.value) || 0;
                if (val > 999) val = 999;
                if (val < 0) val = 0;
                setTemp(val);
              }}
              inputProps={{
                min: 0,
                max: 999,
                style: { textAlign: 'center', fontSize: 24, fontWeight: 'bold' }
              }}
              sx={{
                width: 120,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '& input': {
                    textAlign: 'center',
                    fontSize: 24,
                    fontWeight: 'bold',
                    py: 1.5
                  }
                }
              }}
            />

            <IconButton
              onClick={increment}
              sx={{
                bgcolor: 'var(--appGrey100)',
                '&:hover': { bgcolor: 'var(--appGrey200)' },
                width: 48,
                height: 48
              }}
            >
              <KeyboardArrowUpIcon />
            </IconButton>
          </Box>

          <Divider sx={{ my: 2, borderColor: 'var(--appBorderMedium)' }} />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
            <Button
              fullWidth
              onClick={handleClose}
              variant="outlined"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                py: 0.75,
                borderColor: 'var(--appBorderDark)',
                color: 'var(--appTextSecondary)',
                '&:hover': {
                  borderColor: 'var(--appMainMain)',
                  backgroundColor: 'var(--appMainMain05)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSave}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                py: 0.75,
                backgroundColor: 'var(--appMainMain)',
                '&:hover': {
                  backgroundColor: 'var(--appMain800)'
                }
              }}
            >
              Apply Change
            </Button>
          </Box>
        </Box>
      </Menu>
    </>
  );
}
